import Modem from "../api/Modem";
import ConfigHandle, { Config, ConfigSpec } from "../conf/config";
import Configs, { ConfigListResult } from "../conf/configs";
import NetworkHandle, { Network, NetworkSpec } from "../network/network";
import Networks from "../network/networks";
import SecretHandle, { Secret, SecretSpec } from "../secret/secret";
import Secrets, { SecretListResult } from "../secret/secrets";
import { Service, ServiceSpec } from "../service/service";
import Services, { ServiceListResult } from "../service/services";
import { ClusterVolume, ClusterVolumeSpec } from "../volume/clusterVolume";
import Volumes from "../volume/volumes";
import StackHandle, { Stack, StackScratchSpec, StackServiceSpec, StackSpec } from "./stack";
import { DOCKER_STACK_NAMESPACE_LABEL } from "./stackDeps";
import { Volume, VolumeSpec } from "../volume/volume";

import { execFile } from 'child_process';
import { Readable } from "stream";
import Handle from "../utils/Handle";

import crypto from "crypto";
import { DockerLabels } from "../utils/types";

type StackObjectListResult = Promise<ServiceListResult | ConfigListResult | SecretListResult>;
type StackObject = Service | Config | Secret;

export type StackListResult = Array<Stack>;

const getStackNamed = <Spec extends { Name?: string } | string | undefined>(stackName: string) => (spec: Spec): string => {
    const target = typeof spec === "undefined" ? spec : typeof spec === "string" ? spec : spec.Name;

    if (target && target.startsWith(`${stackName}_`))
        return target;

    return `${stackName}_${target ?? crypto.randomBytes(16).toString("hex")}`;
}

const appendStackLabel = <Spec extends { Name?: string, Labels?: DockerLabels }>(stackName: string) => (spec: Spec): Spec => {
    if (!spec.Labels)
        spec.Labels = {};

    spec.Name! = getStackNamed(stackName)(spec);
    spec.Labels[DOCKER_STACK_NAMESPACE_LABEL] = stackName;
    return spec as Spec;
}

function stackMapReducer(map: Map<string, Array<any>>, object: any): Map<string, Array<StackObject>> {
    if (!object.Labels || !(DOCKER_STACK_NAMESPACE_LABEL in object.Labels))
        return map;

    const stackName = object.Labels![DOCKER_STACK_NAMESPACE_LABEL];

    if (!map.has(stackName))
        map.set(stackName, new Array());

    map.get(stackName)!.push(object);
    return map;
}

function stackMapSpecReducer(map: Map<string, Array<StackObject>>, object: StackObject): Map<string, Array<StackObject>> {
    if (!object.Spec.Labels || !(DOCKER_STACK_NAMESPACE_LABEL in object.Spec.Labels))
        return map;

    const stackName = object.Spec.Labels![DOCKER_STACK_NAMESPACE_LABEL];

    if (!map.has(stackName))
        map.set(stackName, new Array());

    map.get(stackName)!.push(object);
    return map;
}

export default class Stacks {
    public static async list(modem: Modem) {
        const labelFilter = new Map();
        labelFilter.set("label", Array.of(DOCKER_STACK_NAMESPACE_LABEL));
        
        const labelListQueryOptions = {
            filters: labelFilter
        };

        const serviceListPromise = Services.list(modem, labelListQueryOptions) as StackObjectListResult;
        const configListPromise = Configs.list(modem, labelListQueryOptions) as StackObjectListResult;
        const secretListPromise = Secrets.list(modem, labelListQueryOptions) as StackObjectListResult;
        const networkListPromise = Networks.list(modem, labelListQueryOptions);
        const volumeListPromise = Volumes.list(modem, labelListQueryOptions);
        const clusterVolumeListPromise = Volumes.listCluster(modem, labelListQueryOptions);

        const promises = Array.of(
            serviceListPromise,
            configListPromise,
            secretListPromise
        );
        
        const results = await Promise.all(promises);
        const serviceMap: Map<string, Array<StackObject>> = results[0].reduce(stackMapSpecReducer, new Map());
        const configMap: Map<string, Array<StackObject>> = results[1].reduce(stackMapSpecReducer, new Map());
        const secretMap: Map<string, Array<StackObject>> = results[2].reduce(stackMapSpecReducer, new Map());

        const networkMap: Map<string, Array<Network>> = (await networkListPromise).reduce(stackMapReducer, new Map());
        const volumeMap: Map<string, Array<Volume>> = (await volumeListPromise).Volumes.reduce(stackMapReducer, new Map());
        const clusterVolumeMap: Map<string, Array<ClusterVolume>> = (await clusterVolumeListPromise).Volumes.reduce(stackMapReducer, new Map());
        
        const stackLabels = Array.of(
            ...Array.from(serviceMap.keys()),
            ...Array.from(configMap.keys()),
            ...Array.from(secretMap.keys()),
            ...Array.from(networkMap.keys()),
            ...Array.from(volumeMap.keys()),
            ...Array.from(clusterVolumeMap.keys())
        );
        
        const stackNames = new Set(stackLabels);
        const stacks = Array.from(stackNames).map<Stack>(name => {
            return {
                Spec: {
                    Name: name
                },
                Services: serviceMap.has(name) ? Array.from(serviceMap.get(name)!) : new Array(),
                Configs: configMap.has(name) ? Array.from(configMap.get(name)!) : new Array(),
                Secrets: secretMap.has(name) ? Array.from(secretMap.get(name)!) : new Array(),
                Networks: networkMap.has(name) ? Array.from(networkMap.get(name)!) : new Array(),
                Volumes: volumeMap.has(name) ? Array.from(volumeMap.get(name)!) : new Array(),
                ClusterVolumes: clusterVolumeMap.has(name) ? Array.from(clusterVolumeMap.get(name)!) : new Array()
            };
        });

        return stacks as StackListResult;
    }

    public static async create(modem: Modem, spec: StackSpec) {
        await new Promise(function(resolve, reject) {
            if (!spec.Yaml) {
                reject(new Error("Stack cannot be created without valid compose data"));
                return;
            }

            const child = execFile("docker", ["stack", "deploy", "-d", "--compose-file -", spec.Name], function(err, stdout, stderr) {
                if (err)
                    reject(err);

                // TODO maybe do something with the stdout and stderr
            });

            const stream = new Readable();
            stream.push(spec.Yaml);
            stream.push(null);

            if (child.stdin)
                stream.pipe(child.stdin);

            resolve(true);
        });
        
        return new StackHandle(modem, spec.Name);
    }

    private static async getObjectState<HandleType extends Handle>(modem: Modem, oldNames: Array<string>, newNames: Array<string>, existFunc: (modem: Modem, name: string) => Promise<{ exists: boolean, handle?: HandleType }>) {
        const exists = await Promise.all(newNames.map(async name => { return Object.assign(await existFunc(modem, name), { name }); }));

        const crt = exists.filter(value => !value.exists &&  newNames.includes(value.name)).map(value => value.name);
        const upd = exists.filter(value =>  value.exists &&  newNames.includes(value.name) && oldNames.includes(value.name)).reduce((map, value) => { return map.set(value.name, value.handle!); }, new Map<string, HandleType>());
        const rem = exists.filter(value =>  value.exists && !newNames.includes(value.name) && oldNames.includes(value.name)).reduce((map, value) => { return map.set(value.name, value.handle!); }, new Map<string, HandleType>());

        return { crt, upd, rem };
    }

    public static async createFromScratch(modem: Modem, spec: StackScratchSpec) {
        // check if any config, secret or network do not have a provisional id when they are created newly
        const oldState = await Stacks.getHandle(modem, spec.Name).inspect();

        const configState = spec.Configs ? await Stacks.getObjectState(modem, oldState.Configs.map(config => config.Spec).map(getStackNamed(spec.Name)), spec.Configs.map(getStackNamed(spec.Name)), Configs.exists) : null;
        const secretState = spec.Secrets ? await Stacks.getObjectState(modem, oldState.Secrets.map(secret => secret.Spec).map(getStackNamed(spec.Name)), spec.Secrets.map(getStackNamed(spec.Name)), Secrets.exists) : null;
        const networkState = spec.Networks ? await Stacks.getObjectState(modem, oldState.Networks.map(getStackNamed(spec.Name)), spec.Networks.map(getStackNamed(spec.Name)), Networks.existsCluster) : null;
        const volumeState = spec.Volumes ? await Stacks.getObjectState(modem, oldState.ClusterVolumes.map(getStackNamed(spec.Name)), spec.Volumes.filter(spec => spec.Name).map(getStackNamed(spec.Name)), Volumes.existsCluster) : null;
        const serviceState = await Stacks.getObjectState(modem, oldState.Services.map(service => service.Spec).map(getStackNamed(spec.Name)), spec.Services.map(service => service.Spec).map(getStackNamed(spec.Name)), Services.exists);

        const someNewConfigWithoutProvisionalId = spec.Configs?.some(config => typeof config.ProvisionalId === "undefined" && configState?.crt.includes(config.Name));
        const someNewSecretWithoutProvisionalId = spec.Secrets?.some(secret => typeof secret.ProvisionalId === "undefined" && secretState?.crt.includes(secret.Name));
        const someNewNetworkWithoutProvisionalId = spec.Networks?.some(network => typeof network.ProvisionalId === "undefined" && networkState?.crt.includes(network.Name));

        if (someNewConfigWithoutProvisionalId || someNewSecretWithoutProvisionalId || someNewNetworkWithoutProvisionalId)
            throw new Error("Newly created configs, secrets or networks need to specifiy a provisional id in order to match them with the corresponding services or volumes");

        function networkAsClusterNetwork<Spec extends NetworkSpec>(networkSpec: Spec) {
            networkSpec.Scope = "swarm";
            networkSpec.Driver = "overlay";

            return networkSpec;
        }

        // create independed objects
        const provConfigHandleMap = spec.Configs && configState ? (await Promise.all(spec.Configs.map(appendStackLabel(spec.Name)).filter(config => configState.crt.includes(config.Name)).map(async config => { return { handle: await Configs.create(modem, config), provId: config.ProvisionalId }; }))).reduce((map, value) => { return map.set(value.provId, value.handle); }, new Map<string, ConfigHandle>()) : null;
        const provSecretHandleMap = spec.Secrets && secretState ? (await Promise.all(spec.Secrets.map(appendStackLabel(spec.Name)).filter(secret => secretState.crt.includes(secret.Name)).map(async secret => { return { handle: await Secrets.create(modem, secret), provId: secret.ProvisionalId }; }))).reduce((map, value) => { return map.set(value.provId, value.handle); }, new Map<string, SecretHandle>()) : null;
        const provNetworkHandleMap = spec.Networks && networkState ? (await Promise.all(spec.Networks.map(appendStackLabel(spec.Name)).filter(network => networkState.crt.includes(network.Name)).map(networkAsClusterNetwork).map(async network => { return { handle: await Networks.create(modem, network), provId: network.ProvisionalId }; }))).reduce((map, value) => { return map.set(value.provId, value.handle); }, new Map<string, NetworkHandle>()) : null;

        // update independed obejcts
        if (spec.Configs && configState) await Promise.all(spec.Configs.map(appendStackLabel(spec.Name)).filter(config => Array.from(configState.upd.keys()).includes(config.Name)).map(config => configState.upd.get(config.Name)?.update(config)));
        if (spec.Secrets && secretState) await Promise.all(spec.Secrets.map(appendStackLabel(spec.Name)).filter(secret => Array.from(secretState.upd.keys()).includes(secret.Name)).map(secret => secretState.upd.get(secret.Name)?.update(secret)));
        // network does not have an update method

        function replaceClusterVolumeSecretProvisional(volumeSpec: ClusterVolumeSpec) {
            if (provSecretHandleMap) volumeSpec.ClusterVolumeSpec.Secrets = volumeSpec.ClusterVolumeSpec.Secrets?.map(secret => provSecretHandleMap.has(secret.Secret) ? Object.assign(secret, { Secret: provSecretHandleMap.get(secret.Secret)!.id }) : secret);
            return volumeSpec;
        }

        function replaceServiceProvisional(serviceSpec: StackServiceSpec) {
            // replace config and secret provisional ids
            if (provConfigHandleMap) serviceSpec.Spec.TaskTemplate.ContainerSpec!.Configs = serviceSpec.Spec.TaskTemplate.ContainerSpec?.Configs?.map(config => provConfigHandleMap.has(config.ConfigID) ? Object.assign(config, { ConfigID: provConfigHandleMap.get(config.ConfigID)!.id, ConfigName: getStackNamed(spec.Name)(config.ConfigName) }) : config);
            if (provSecretHandleMap) serviceSpec.Spec.TaskTemplate.ContainerSpec!.Secrets = serviceSpec.Spec.TaskTemplate.ContainerSpec?.Secrets?.map(secret => provSecretHandleMap.has(secret.SecretID) ? Object.assign(secret, { SecretID: provSecretHandleMap.get(secret.SecretID)!.id, SecretName: getStackNamed(spec.Name)(secret.SecretName) }) : secret);
            
            // replace network provisional ids
            if (provNetworkHandleMap) serviceSpec.Spec.TaskTemplate.Networks = serviceSpec.Spec.TaskTemplate.Networks?.map(network => provNetworkHandleMap.has(network.Target) ? Object.assign(network, { Target: provNetworkHandleMap.get(network.Target) }) : network);
            
            // replace volume mount names with stack name appended
            serviceSpec.Spec.TaskTemplate.ContainerSpec!.Mounts = serviceSpec.Spec.TaskTemplate.ContainerSpec?.Mounts?.map(mount => (mount.Type === "volume" || mount.Type === "cluster") && mount.Source ? Object.assign(mount, { Source: getStackNamed(spec.Name)(mount.Source)}) : mount)
            return serviceSpec;
        }

        // create and update cluster volumes
        if (spec.Volumes && volumeState) {
            await Promise.all(spec.Volumes.map(appendStackLabel(spec.Name)).filter(volume => volumeState.crt.includes(volume.Name!)).map(replaceClusterVolumeSecretProvisional).map(volume => Volumes.createCluster(modem, volume)));
            await Promise.all(spec.Volumes.map(appendStackLabel(spec.Name)).filter(volume => Array.from(volumeState.upd.keys()).includes(volume.Name!)).map(replaceClusterVolumeSecretProvisional).map(volume => volumeState.upd.get(volume.Name!)?.update(volume)));
        }

        // create update and remove services
        await Promise.all(spec.Services.map(replaceServiceProvisional).filter(service => serviceState.crt.includes(getStackNamed(spec.Name)(service.Spec.Name))).map(service => Services.create(modem, appendStackLabel<ServiceSpec>(spec.Name)(service.Spec), service.RegistryAuth)));
        await Promise.all(spec.Services.map(replaceServiceProvisional).filter(service => Array.from(serviceState.upd.keys()).includes(getStackNamed(spec.Name)(service.Spec.Name))).map(service => serviceState.upd.get(getStackNamed(spec.Name)(service.Spec.Name))?.update(appendStackLabel<ServiceSpec>(spec.Name)(service.Spec), { registryAuthFrom: service.RegistryAuth ? "spec" : "previous-spec" }, service.RegistryAuth)));
        await Promise.all(spec.Services.map(replaceServiceProvisional).filter(service => Array.from(serviceState.rem.keys()).includes(getStackNamed(spec.Name)(service.Spec.Name))).map(service => serviceState.rem.get(getStackNamed(spec.Name)(service.Spec.Name))?.remove()));
        
        // remove cluster volumes
        if (spec.Volumes && volumeState) await Promise.all(spec.Volumes.map(appendStackLabel(spec.Name)).filter(volume => Array.from(volumeState.rem.keys()).includes(volume.Name!)).map(volume => volumeState.rem.get(volume.Name!)?.remove()));
        
        // remove networks, secrets and configs
        if (spec.Configs && configState) await Promise.all(spec.Configs.map(appendStackLabel(spec.Name)).filter(config => Array.from(configState.rem.keys()).includes(config.Name)).map(config => configState.rem.get(config.Name)?.remove()));
        if (spec.Secrets && secretState) await Promise.all(spec.Secrets.map(appendStackLabel(spec.Name)).filter(secret => Array.from(secretState.rem.keys()).includes(secret.Name)).map(secret => secretState.rem.get(secret.Name)?.remove()));
        if (spec.Networks && networkState) await Promise.all(spec.Networks.map(appendStackLabel(spec.Name)).filter(network => Array.from(networkState.rem.keys()).includes(network.Name)).map(network => networkState.rem.get(network.Name)?.remove()));
        
        return new StackHandle(modem, spec.Name);
    }

    public static getHandle(modem: Modem, name: string) {
        return new StackHandle(modem, name);
    }
}